import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Timeslot } from "../types";
import { timeslotsApi } from "../api/client";
import "./TimeslotManager.css";

interface TimeslotManagerProps {
  timeslots: Timeslot[];
  schoolId: number;
  onTimeslotsChange: () => void;
  canEdit?: boolean;
}

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export function TimeslotManager({
  timeslots,
  schoolId,
  onTimeslotsChange,
  canEdit = true,
}: TimeslotManagerProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [day, setDay] = useState("monday");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("09:30");
  const [recurring, setRecurring] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenModal = () => {
    setDay("monday");
    setStartTime("09:00");
    setEndTime("09:30");
    setRecurring(true);
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!startTime) {
      setError(t("timeslots.timeRequired"));
      return;
    }
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    const duration = endMin - startMin;
    if (duration <= 0) {
      setError(t("timeslots.timeRangeInvalid"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await timeslotsApi.create({
        day,
        start_time: startTime,
        duration,
        recurring,
        school_id: schoolId,
      });
      onTimeslotsChange();
      handleCloseModal();
    } catch (err) {
      console.error("Failed to create timeslot:", err);
      setError(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (timeslot: Timeslot) => {
    if (!window.confirm(t("timeslots.confirmDelete"))) {
      return;
    }

    setLoading(true);
    try {
      await timeslotsApi.delete(timeslot.id);
      onTimeslotsChange();
    } catch (err) {
      console.error("Failed to delete timeslot:", err);
      alert(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  // Group timeslots by day
  const timeslotsByDay = DAYS.map((day) => ({
    day,
    slots: timeslots.filter((slot) => slot.day.toLowerCase() === day),
  }));

  return (
    <div className="timeslot-manager">
      <div className="view-header">
        <h2>{t("timeslots.title")}</h2>
        <button
          className="btn-primary"
          onClick={handleOpenModal}
          disabled={!canEdit}
        >
          + {t("timeslots.addTimeslot")}
        </button>
      </div>

      {timeslots.length === 0 ? (
        <div className="card empty-state">
          <p>{t("timeslots.noTimeslots")}</p>
          <button
            className="btn-primary"
            onClick={handleOpenModal}
            disabled={!canEdit}
          >
            {t("timeslots.addFirstTimeslot")}
          </button>
        </div>
      ) : (
        <div className="timeslots-week-view">
          {timeslotsByDay.map(({ day, slots }) => (
            <div key={day} className="day-section">
              <h3 className="day-section-header">{t(`days.${day}`)}</h3>
              {slots.length === 0 ? (
                <div className="day-empty">
                  <p>{t("timeslots.noDaySlots")}</p>
                </div>
              ) : (
                <div className="timeslots-list">
                  {slots.map((slot) => (
                    <div key={slot.id} className="timeslot-item">
                      <div className="timeslot-item-time">
                        <span className="time">
                          {slot.start_time}–
                          {(() => {
                            const [h, m] = slot.start_time
                              .slice(0, 5)
                              .split(":")
                              .map(Number);
                            const s = h * 60 + m;
                            const e = (s + slot.duration) % (24 * 60);
                            const eh = Math.floor(e / 60),
                              em = e % 60;
                            const pad = (n: number) =>
                              n < 10 ? `0${n}` : `${n}`;
                            return `${pad(eh)}:${pad(em)}`;
                          })()}
                        </span>
                      </div>
                      <div className="timeslot-item-badge">
                        {slot.recurring
                          ? t("timeslots.recurring")
                          : t("timeslots.oneTime")}
                      </div>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDelete(slot)}
                        disabled={loading || !canEdit}
                        title={t("common.delete")}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("timeslots.addTimeslot")}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="day">{t("timeslots.day")}</label>
                <select
                  id="day"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  disabled={loading}
                >
                  {DAYS.map((d) => (
                    <option key={d} value={d}>
                      {t(`days.${d}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="start-time">{t("timeslots.startTime")}</label>
                <input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={loading || !canEdit}
                />
              </div>

              <div className="form-group">
                <label htmlFor="end-time">{t("timeslots.endTime")}</label>
                <input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  disabled={loading || !canEdit}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={recurring}
                    onChange={(e) => setRecurring(e.target.checked)}
                    disabled={loading || !canEdit}
                  />
                  <span>{t("timeslots.recurringLabel")}</span>
                </label>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !canEdit}
                >
                  {loading ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
